import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { SubmitPriceChangesInput } from "@/graphql/codegen/graphql"
import { SubmitPricingForm } from "@/screens/Pricing/components/forms/SubmitPricingForm"
import type { PriceChangeSubmitContext } from "@/screens/Pricing/components/PriceChangeTables"
import { useSubmitPriceChanges } from "@/screens/Pricing/hooks/useSubmitPriceChanges"
import type { SubmitPriceChangeSchemaFormValues } from "@/screens/Pricing/schema/SumbitPriceChangeSchema"
import { SubmitPriceChangeSchema } from "@/screens/Pricing/schema/SumbitPriceChangeSchema"

interface SubmitPricingDialogProps {
  onClose: () => void
  submitContext: PriceChangeSubmitContext
}

const SubmitPricingDialog: FC<SubmitPricingDialogProps> = ({ onClose, submitContext }) => {
  const { submitPriceChanges, isSubmitting } = useSubmitPriceChanges()
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const methods = useForm<SubmitPriceChangesInput>({
    defaultValues: {
      reasonForUpdate: "",
    },
    mode: "onChange",
    resolver: zodResolver(SubmitPriceChangeSchema),
  })

  const {
    formState: { isDirty, isValid },
  } = methods

  const confirmCancel = () => {
    onClose()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const handleSuccess = async (data: SubmitPriceChangeSchemaFormValues) => {
    const isSuccess = await submitPriceChanges({
      // TODO: clarify later with BE when submission is implemented
      assignedToId: "",
      filter: submitContext.filter,
      manualOverrides: submitContext.priceChangeItems,
      reasonForUpdate: data.reasonForUpdate ?? "",
    })
    if (!isSuccess) {
      return
    }
    onClose()
  }

  return (
    <>
      {isCancelConfirmationOpen && (
        <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
      )}
      <Dialog open onClose={handleCancel} fullWidth>
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>Submit Price Changes</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancel} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <SubmitPricingForm />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancel}>
              Cancel
            </Button>
            <Button disabled={!isValid || !isDirty || isSubmitting} onClick={methods.handleSubmit(handleSuccess)}>
              Continue
            </Button>
          </DialogActions>
        </>
      </Dialog>
    </>
  )
}

export default SubmitPricingDialog
